import { useState } from 'react';
import { IAccreditationApplication } from 'interfaces/IAccreditationApplication';
import {
    CreateAccreditationApplicationCommandInput,
    DeleteAccreditationApplicationCommandInput,
    LockOwner,
    ProgressAccreditationApplicationCommandInput,
    UpdateAccreditationApplicationCommandInput,
} from '../graphql/__generated__/graphql';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
    CREATE_APPLICATION,
    DELETE_APPLICATION,
    GET_ACCREDITATION_APPLICATION,
    PROGRESS_APPLICATION,
    UPDATE_APPLICATION,
} from '../graphql/member';

export const useAccreditationApplication = () => {
    const [updating, setUpdating] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    // Mutations
    const [deleteApplicationMutation] = useMutation(DELETE_APPLICATION);
    const [progressApplicationMutation] = useMutation(PROGRESS_APPLICATION);
    const [createApplicationMutation] = useMutation(CREATE_APPLICATION);
    const [updateApplicationMutation] = useMutation(UPDATE_APPLICATION);

    // Queries
    const [fetchApplicationQuery] = useLazyQuery(GET_ACCREDITATION_APPLICATION, {
        fetchPolicy: 'no-cache',
    });

    // Submit an application for review
    const submitApplication = async (
        submittedApplication: IAccreditationApplication,
        nextLockOwner: LockOwner = LockOwner.Supervisor
    ): Promise<IAccreditationApplication> => {
        try {
            setUpdating(true);
            const application: ProgressAccreditationApplicationCommandInput = {
                accreditationApplicationId: submittedApplication.accreditationApplicationId,
                nextLockOwner,
            };

            const { data } = await progressApplicationMutation({
                variables: { application },
            });

            return data.accreditationApplicationProgress;
        } catch (error) {
            console.error('Error submitting application:', error);
            setError(error instanceof Error ? error : new Error(String(error)));
            throw error;
        } finally {
            setUpdating(false);
        }
    };

    const fetchApplication = async (
        accreditationApplicationId: string
    ): Promise<IAccreditationApplication | null> => {
        setLoading(true);
        try {
            const { data } = await fetchApplicationQuery({
                variables: {
                    applicationId: accreditationApplicationId,
                },
            });
            return data.accreditationApplication;
        } catch (error) {
            console.error('Error fetching application:', error);
            setError(error instanceof Error ? error : new Error(String(error)));
            return null;
        } finally {
            setLoading(false);
        }
    };

    // Create a new application
    const createApplication = async ({
        accreditationType,
        isRenewal,
    }: IAccreditationApplication): Promise<IAccreditationApplication> => {
        try {
            setUpdating(true);
            const application: CreateAccreditationApplicationCommandInput = {
                accreditationTypeId: accreditationType.accreditationTypeId,
                isRenewal,
            };

            const { data } = await createApplicationMutation({
                variables: { application },
            });

            return data.accreditationApplicationCreate as IAccreditationApplication;
        } catch (error) {
            console.error('Error creating application:', error);
            setError(error instanceof Error ? error : new Error(String(error)));
            throw error;
        } finally {
            setUpdating(false);
        }
    };

    // Delete an application
    const deleteApplication = async (
        deletedApplication: IAccreditationApplication
    ): Promise<void> => {
        setUpdating(true);
        try {
            const application: DeleteAccreditationApplicationCommandInput = {
                accreditationApplicationId: deletedApplication.accreditationApplicationId,
            };

            await deleteApplicationMutation({
                variables: { application },
            });
        } catch (error) {
            console.error('Error deleting application:', error);
            setError(error instanceof Error ? error : new Error(String(error)));
            throw error;
        } finally {
            setUpdating(false);
        }
    };

    const upsertApplication = async (application: IAccreditationApplication) => {
        if (application.accreditationApplicationId) {
            return await updateApplication(application);
        } else {
            return await createApplication(application);
        }
    };

    // Update an existing application
    const updateApplication = async (
        updatedApplication: IAccreditationApplication
    ): Promise<IAccreditationApplication> => {
        try {
            setUpdating(true);
            const application: UpdateAccreditationApplicationCommandInput = {
                accreditationApplicationId: updatedApplication.accreditationApplicationId,
                jsonData: updatedApplication.jsonData,
            };

            const { data } = await updateApplicationMutation({
                variables: { application },
            });

            return data.accreditationApplicationUpdate as IAccreditationApplication;
        } catch (error) {
            console.error('Error updating application:', error);
            setError(error instanceof Error ? error : new Error(String(error)));
            throw error;
        } finally {
            setUpdating(false);
        }
    };

    return {
        models: {
            error,
            loading,
            updating,
        },
        operations: {
            createApplication,
            deleteApplication,
            fetchApplication,
            submitApplication,
            updateApplication,
            upsertApplication,
        },
    };
};
